import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  BarChart3,
  Edit2,
  Image,
  Mail,
  MessageSquare,
  Package,
  Plus,
  RefreshCw,
  Save,
  Settings,
  ShoppingBag,
  Trash2,
  X,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import {
  createArtwork,
  deleteArtwork,
  getAdminStats,
  getArtworks,
  getContactMessages,
  getOrders,
  getStoreSettings,
  markContactMessageRead,
  saveStoreSettings,
  updateArtwork,
  updateOrderStatus,
  uploadImage,
  type AdminStats,
  type Artwork,
  type ContactMessage,
  type Order,
  type StoreSettings,
} from "@/lib/api";
import { formatPrice } from "@/lib/utils";

type Tab = "dashboard" | "artworks" | "add" | "orders" | "messages" | "settings";

const emptySettings: StoreSettings = {
  announcement_bar: "",
  contact_email: "",
  contact_phone: "",
  studio_address: "",
  shipping_note: "",
  checkout_note: "",
};

export default function Admin() {
  const { user, initialized, checkAuth } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState<Tab>("dashboard");
  const [stats, setStats] = useState<AdminStats | null>(null);
  const [artworks, setArtworks] = useState<Artwork[]>([]);
  const [orders, setOrders] = useState<Order[]>([]);
  const [messages, setMessages] = useState<ContactMessage[]>([]);
  const [settings, setSettings] = useState<StoreSettings>(emptySettings);
  const [editing, setEditing] = useState<Artwork | null>(null);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);

  const tabs: { key: Tab; label: string; icon: React.ReactNode }[] = [
    { key: "dashboard", label: "Dashboard", icon: <BarChart3 size={17} /> },
    { key: "artworks", label: "Productos", icon: <Image size={17} /> },
    { key: "add", label: "Nuevo", icon: <Plus size={17} /> },
    { key: "orders", label: "Pedidos", icon: <Package size={17} /> },
    { key: "messages", label: "Mensajes", icon: <MessageSquare size={17} /> },
    { key: "settings", label: "Tienda", icon: <Settings size={17} /> },
  ];

  const filteredArtworks = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return artworks;
    return artworks.filter((artwork) =>
      [artwork.title_en, artwork.title_it, artwork.category, artwork.status].some((value) =>
        String(value || "").toLowerCase().includes(term)
      )
    );
  }, [artworks, search]);

  useEffect(() => {
    if (!initialized) {
      checkAuth();
      return;
    }
    if (!user || user.role !== "admin") {
      navigate("/login");
      return;
    }
    void loadData();
  }, [initialized, user, navigate, checkAuth]);

  async function loadData() {
    setLoading(true);
    try {
      const [statsData, artworksData, ordersData, messagesData, settingsData] = await Promise.all([
        getAdminStats(),
        getArtworks({ status: "all", limit: 200 }),
        getOrders(),
        getContactMessages(),
        getStoreSettings(),
      ]);
      setStats(statsData);
      setArtworks(artworksData.artworks);
      setOrders(ordersData.orders);
      setMessages(messagesData.messages);
      setSettings(settingsData);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "No se pudo cargar el admin");
    } finally {
      setLoading(false);
    }
  }

  if (!initialized || loading) {
    return (
      <div className="pt-24 pb-16">
        <div className="container">
          <p style={{ color: "var(--color-text-muted)" }}>Cargando admin...</p>
        </div>
      </div>
    );
  }

  if (!user || user.role !== "admin") return null;

  return (
    <div className="pt-24 pb-16">
      <div className="container">
        <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <p className="mb-2 text-xs uppercase tracking-[0.35em]" style={{ color: "var(--color-text-light)" }}>
              Dalila studio
            </p>
            <h1 className="text-3xl" style={{ fontFamily: "var(--font-display)", color: "var(--color-charcoal)" }}>
              Admin
            </h1>
          </div>
          <button onClick={() => void loadData()} className="btn-secondary flex w-fit items-center gap-2">
            <RefreshCw size={16} />
            Actualizar
          </button>
        </div>

        <div className="mb-8 flex flex-wrap gap-2 border-b" style={{ borderColor: "var(--color-border)" }}>
          {tabs.map((item) => (
            <button
              key={item.key}
              onClick={() => {
                setTab(item.key);
                setEditing(null);
              }}
              className={`flex items-center gap-2 border-b-2 px-3 py-3 text-sm transition-colors ${
                tab === item.key
                  ? "border-[var(--color-terracotta)] text-[var(--color-terracotta)]"
                  : "border-transparent text-[var(--color-text-muted)] hover:text-[var(--color-charcoal)]"
              }`}
            >
              {item.icon}
              {item.label}
            </button>
          ))}
        </div>

        {tab === "dashboard" && stats && <Dashboard stats={stats} />}

        {tab === "artworks" && (
          <section className="space-y-5">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Buscar por título, categoría o estado"
                className="w-full border bg-white px-4 py-3 text-sm focus:outline-none sm:max-w-sm"
                style={{ borderColor: "var(--color-border)" }}
              />
              <button onClick={() => setTab("add")} className="btn-primary flex w-fit items-center gap-2">
                <Plus size={16} />
                Añadir producto
              </button>
            </div>

            {editing && (
              <AdminPanel title={`Editar: ${editing.title_en}`} onClose={() => setEditing(null)}>
                <ArtworkForm
                  artwork={editing}
                  onSuccess={async () => {
                    setEditing(null);
                    await loadData();
                  }}
                />
              </AdminPanel>
            )}

            <div className="grid gap-4">
              {filteredArtworks.map((artwork) => (
                <div key={artwork.id} className="grid gap-4 border bg-white p-4 sm:grid-cols-[84px_1fr_auto]" style={{ borderColor: "var(--color-border)" }}>
                  <img src={artwork.image_url || "/placeholder-artwork.jpg"} alt={artwork.title_en} className="h-24 w-24 object-cover sm:h-20 sm:w-20" />
                  <div className="min-w-0">
                    <div className="mb-1 flex flex-wrap items-center gap-2">
                      <p className="font-medium" style={{ color: "var(--color-charcoal)" }}>{artwork.title_en}</p>
                      {artwork.featured ? <Badge>featured</Badge> : null}
                    </div>
                    <p className="text-xs uppercase tracking-[0.22em]" style={{ color: "var(--color-text-light)" }}>
                      {artwork.category} · {artwork.status} · stock {artwork.stock_quantity ?? 1}
                    </p>
                    <p className="mt-2 text-sm" style={{ color: "var(--color-text-muted)" }}>
                      {formatPrice(artwork.price, artwork.currency)}
                    </p>
                  </div>
                  <div className="flex items-start gap-2">
                    <button onClick={() => setEditing(artwork)} className="p-2 hover:text-[var(--color-terracotta)]" style={{ color: "var(--color-text-muted)" }} aria-label="Editar producto">
                      <Edit2 size={17} />
                    </button>
                    <button
                      onClick={async () => {
                        if (!confirm("¿Eliminar este producto?")) return;
                        await deleteArtwork(artwork.id);
                        toast.success("Producto eliminado");
                        await loadData();
                      }}
                      className="p-2 hover:text-red-500"
                      style={{ color: "var(--color-text-muted)" }}
                      aria-label="Eliminar producto"
                    >
                      <Trash2 size={17} />
                    </button>
                  </div>
                </div>
              ))}
              {filteredArtworks.length === 0 && <EmptyState text="No hay productos con ese filtro." />}
            </div>
          </section>
        )}

        {tab === "add" && (
          <AdminPanel title="Nuevo producto">
            <ArtworkForm onSuccess={async () => {
              toast.success("Producto creado");
              setTab("artworks");
              await loadData();
            }} />
          </AdminPanel>
        )}

        {tab === "orders" && <OrdersPanel orders={orders} onChange={loadData} />}
        {tab === "messages" && <MessagesPanel messages={messages} onChange={loadData} />}
        {tab === "settings" && <SettingsPanel settings={settings} setSettings={setSettings} onSaved={loadData} />}
      </div>
    </div>
  );
}

function Dashboard({ stats }: { stats: AdminStats }) {
  return (
    <div className="space-y-8">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard label="Productos" value={stats.totalArtworks} icon={<ShoppingBag size={18} />} />
        <StatCard label="Disponibles" value={stats.availableArtworks} icon={<Image size={18} />} />
        <StatCard label="Pedidos activos" value={stats.pendingOrders ?? 0} icon={<Package size={18} />} />
        <StatCard label="Mensajes sin leer" value={stats.unreadMessages} icon={<Mail size={18} />} />
        <StatCard label="Vendidos" value={stats.soldArtworks} />
        <StatCard label="Reservados" value={stats.reservedArtworks ?? 0} />
        <StatCard label="Pedidos pagados" value={stats.totalOrders} />
        <StatCard label="Revenue" value={formatPrice(stats.totalRevenue)} />
      </div>

      <AdminPanel title="Últimos pedidos">
        <div className="space-y-3">
          {(stats.recentOrders || []).map((order) => (
            <OrderRow key={order.id} order={order} compact />
          ))}
          {(!stats.recentOrders || stats.recentOrders.length === 0) && <EmptyState text="Todavía no hay pedidos." />}
        </div>
      </AdminPanel>
    </div>
  );
}

function ArtworkForm({ artwork, onSuccess }: { artwork?: Artwork; onSuccess: () => void | Promise<void> }) {
  const [saving, setSaving] = useState(false);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [form, setForm] = useState({
    title_it: artwork?.title_it || "",
    title_en: artwork?.title_en || "",
    description_it: artwork?.description_it || "",
    description_en: artwork?.description_en || "",
    category: artwork?.category || "original",
    technique: artwork?.technique || "",
    dimensions: artwork?.dimensions || "",
    year: artwork?.year || new Date().getFullYear(),
    price: artwork?.price || 0,
    currency: artwork?.currency || "EUR",
    status: artwork?.status || "available",
    stock_quantity: artwork?.stock_quantity ?? (artwork?.category === "print" ? 10 : 1),
    featured: Boolean(artwork?.featured),
    image_key: artwork?.image_key || null,
    image_url: artwork?.image_url || null,
    additional_images: artwork?.additional_images || null,
  });

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    setSaving(true);
    try {
      let nextForm = form;
      if (imageFile) {
        const upload = await uploadImage(imageFile, "artworks");
        nextForm = { ...nextForm, image_key: upload.key, image_url: upload.url };
      }

      const payload = {
        ...nextForm,
        year: Number(nextForm.year) || null,
        price: Number(nextForm.price) || 0,
        stock_quantity: Number(nextForm.stock_quantity) || 0,
        featured: nextForm.featured ? 1 : 0,
      } as Partial<Artwork>;

      if (artwork) {
        await updateArtwork(artwork.id, payload);
        toast.success("Producto actualizado");
      } else {
        await createArtwork(payload);
        toast.success("Producto creado");
      }
      await onSuccess();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "No se pudo guardar el producto");
    } finally {
      setSaving(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className="grid gap-5">
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Título IT *"><input required value={form.title_it} onChange={(e) => setForm({ ...form, title_it: e.target.value })} className="admin-input" /></Field>
        <Field label="Título EN *"><input required value={form.title_en} onChange={(e) => setForm({ ...form, title_en: e.target.value })} className="admin-input" /></Field>
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Descripción IT"><textarea rows={4} value={form.description_it} onChange={(e) => setForm({ ...form, description_it: e.target.value })} className="admin-input resize-none" /></Field>
        <Field label="Descripción EN"><textarea rows={4} value={form.description_en} onChange={(e) => setForm({ ...form, description_en: e.target.value })} className="admin-input resize-none" /></Field>
      </div>
      <div className="grid gap-4 sm:grid-cols-4">
        <Field label="Categoría"><select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value as Artwork["category"] })} className="admin-input"><option value="original">Original</option><option value="print">Print</option></select></Field>
        <Field label="Estado"><select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value as Artwork["status"] })} className="admin-input"><option value="available">Disponible</option><option value="reserved">Reservado</option><option value="sold">Vendido</option></select></Field>
        <Field label="Stock"><input type="number" min={0} value={form.stock_quantity} onChange={(e) => setForm({ ...form, stock_quantity: Number(e.target.value) })} className="admin-input" /></Field>
        <Field label="Año"><input type="number" value={form.year} onChange={(e) => setForm({ ...form, year: Number(e.target.value) })} className="admin-input" /></Field>
      </div>
      <div className="grid gap-4 sm:grid-cols-4">
        <Field label="Precio"><input type="number" min={0} step="0.01" required value={form.price} onChange={(e) => setForm({ ...form, price: Number(e.target.value) })} className="admin-input" /></Field>
        <Field label="Moneda"><input value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value.toUpperCase() })} className="admin-input" /></Field>
        <Field label="Técnica"><input value={form.technique} onChange={(e) => setForm({ ...form, technique: e.target.value })} className="admin-input" /></Field>
        <Field label="Medidas"><input value={form.dimensions} onChange={(e) => setForm({ ...form, dimensions: e.target.value })} className="admin-input" /></Field>
      </div>
      <div className="grid gap-4 sm:grid-cols-[160px_1fr]">
        <div>{form.image_url ? <img src={form.image_url} alt="" className="h-40 w-40 object-cover" /> : <div className="flex h-40 w-40 items-center justify-center border text-sm" style={{ borderColor: "var(--color-border)", color: "var(--color-text-light)" }}>Sin imagen</div>}</div>
        <div className="space-y-4">
          <Field label="Imagen principal"><input type="file" accept="image/jpeg,image/png,image/webp" onChange={(e) => setImageFile(e.target.files?.[0] || null)} className="w-full text-sm" /></Field>
          <label className="flex items-center gap-2 text-sm" style={{ color: "var(--color-text-muted)" }}>
            <input type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} />
            Destacado en la web
          </label>
        </div>
      </div>
      <button type="submit" disabled={saving} className="btn-primary flex w-fit items-center gap-2 disabled:opacity-50">
        <Save size={16} />
        {saving ? "Guardando..." : artwork ? "Guardar cambios" : "Crear producto"}
      </button>
    </form>
  );
}

function OrdersPanel({ orders, onChange }: { orders: Order[]; onChange: () => void | Promise<void> }) {
  return (
    <AdminPanel title="Pedidos">
      <div className="space-y-3">
        {orders.map((order) => (
          <OrderRow key={order.id} order={order} onChange={onChange} />
        ))}
        {orders.length === 0 && <EmptyState text="No hay pedidos todavía." />}
      </div>
    </AdminPanel>
  );
}

function OrderRow({ order, onChange, compact }: { order: Order; onChange?: () => void | Promise<void>; compact?: boolean }) {
  const [saving, setSaving] = useState(false);

  async function handleStatus(status: Order["status"]) {
    if (!onChange) return;
    setSaving(true);
    try {
      await updateOrderStatus(order.id, status);
      toast.success("Estado actualizado");
      await onChange();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "No se pudo actualizar el pedido");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="grid gap-3 border p-4 sm:grid-cols-[1fr_auto]" style={{ borderColor: "var(--color-border)" }}>
      <div>
        <p className="font-medium" style={{ color: "var(--color-charcoal)" }}>Pedido #{order.id} · {formatPrice(order.total_amount, order.currency)}</p>
        <p className="mt-1 text-sm" style={{ color: "var(--color-text-muted)" }}>{order.item_titles || "Sin detalle"} · {order.customer_email || "sin email"}</p>
        {!compact && order.shipping_address ? <p className="mt-2 text-xs" style={{ color: "var(--color-text-light)" }}>{order.shipping_address}</p> : null}
      </div>
      {compact ? <Badge>{order.status}</Badge> : (
        <select disabled={saving} value={order.status} onChange={(event) => void handleStatus(event.target.value as Order["status"])} className="admin-input min-w-36">
          {["pending", "paid", "processing", "shipped", "delivered", "cancelled", "refunded"].map((status) => <option key={status} value={status}>{status}</option>)}
        </select>
      )}
    </div>
  );
}

function MessagesPanel({ messages, onChange }: { messages: ContactMessage[]; onChange: () => void | Promise<void> }) {
  return (
    <AdminPanel title="Mensajes">
      <div className="space-y-3">
        {messages.map((message) => (
          <div key={message.id} className="border p-4" style={{ borderColor: message.read ? "var(--color-border)" : "var(--color-terracotta)" }}>
            <div className="mb-2 flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <p className="font-medium" style={{ color: "var(--color-charcoal)" }}>{message.subject || "Sin asunto"}</p>
                <p className="text-sm" style={{ color: "var(--color-text-muted)" }}>{message.name} · {message.email}</p>
              </div>
              {!message.read && <button onClick={async () => { await markContactMessageRead(message.id); await onChange(); }} className="btn-secondary text-xs">Marcar leído</button>}
            </div>
            <p className="whitespace-pre-wrap text-sm leading-relaxed" style={{ color: "var(--color-text-muted)" }}>{message.message}</p>
          </div>
        ))}
        {messages.length === 0 && <EmptyState text="No hay mensajes." />}
      </div>
    </AdminPanel>
  );
}

function SettingsPanel({ settings, setSettings, onSaved }: { settings: StoreSettings; setSettings: (settings: StoreSettings) => void; onSaved: () => void | Promise<void> }) {
  const [saving, setSaving] = useState(false);

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    setSaving(true);
    try {
      const saved = await saveStoreSettings(settings);
      setSettings(saved);
      toast.success("Configuración guardada");
      await onSaved();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "No se pudo guardar la configuración");
    } finally {
      setSaving(false);
    }
  }

  return (
    <AdminPanel title="Configuración de tienda">
      <form onSubmit={handleSubmit} className="grid gap-4">
        <Field label="Barra de anuncio"><input value={settings.announcement_bar} onChange={(e) => setSettings({ ...settings, announcement_bar: e.target.value })} className="admin-input" /></Field>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Email de contacto"><input value={settings.contact_email} onChange={(e) => setSettings({ ...settings, contact_email: e.target.value })} className="admin-input" /></Field>
          <Field label="Teléfono"><input value={settings.contact_phone} onChange={(e) => setSettings({ ...settings, contact_phone: e.target.value })} className="admin-input" /></Field>
        </div>
        <Field label="Dirección / estudio"><textarea rows={3} value={settings.studio_address} onChange={(e) => setSettings({ ...settings, studio_address: e.target.value })} className="admin-input resize-none" /></Field>
        <Field label="Nota de envío"><textarea rows={3} value={settings.shipping_note} onChange={(e) => setSettings({ ...settings, shipping_note: e.target.value })} className="admin-input resize-none" /></Field>
        <Field label="Nota de checkout"><textarea rows={3} value={settings.checkout_note} onChange={(e) => setSettings({ ...settings, checkout_note: e.target.value })} className="admin-input resize-none" /></Field>
        <button type="submit" disabled={saving} className="btn-primary flex w-fit items-center gap-2 disabled:opacity-50">
          <Save size={16} />
          {saving ? "Guardando..." : "Guardar configuración"}
        </button>
      </form>
    </AdminPanel>
  );
}

function AdminPanel({ title, children, onClose }: { title: string; children: React.ReactNode; onClose?: () => void }) {
  return (
    <section className="border bg-white p-5 sm:p-6" style={{ borderColor: "var(--color-border)" }}>
      <div className="mb-5 flex items-center justify-between gap-4">
        <h2 className="text-xl" style={{ fontFamily: "var(--font-display)", color: "var(--color-charcoal)" }}>{title}</h2>
        {onClose ? <button onClick={onClose} className="p-1" aria-label="Cerrar"><X size={18} /></button> : null}
      </div>
      {children}
    </section>
  );
}

function StatCard({ label, value, icon }: { label: string; value: string | number; icon?: React.ReactNode }) {
  return (
    <div className="border bg-white p-5" style={{ borderColor: "var(--color-border)" }}>
      <div className="mb-4 flex items-center justify-between" style={{ color: "var(--color-text-light)" }}>
        <p className="text-xs uppercase tracking-[0.25em]">{label}</p>
        {icon}
      </div>
      <p className="text-2xl" style={{ fontFamily: "var(--font-display)", color: "var(--color-charcoal)" }}>{value}</p>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-sm" style={{ color: "var(--color-text-muted)" }}>{label}</span>
      {children}
    </label>
  );
}

function Badge({ children }: { children: React.ReactNode }) {
  return <span className="border px-2 py-0.5 text-[10px] uppercase tracking-[0.18em]" style={{ borderColor: "var(--color-border)", color: "var(--color-text-light)" }}>{children}</span>;
}

function EmptyState({ text }: { text: string }) {
  return <p className="border px-4 py-8 text-center text-sm" style={{ borderColor: "var(--color-border)", color: "var(--color-text-muted)" }}>{text}</p>;
}
